// Removes what nobody needs to debug a report: sign-in details, bank credentials, full account and government ids, and
// contact details. Everything else stays, so a saved state file still loads and behaves like the customer's.

const MASK = 'xxx';

const CREDENTIAL_KEY =
  /tokens?$|secret|password|passcode|passphrase|credential|authorization|cookie|signature|api[-_]?key|private[-_]?key|session[-_]?states?$|answers$|birth|tax[-_]?id|employer[-_]?id|national[-_]?id|passport|license|routing[-_]?number|(?:account|card)[-_]?number(?![-_]?masked)|account[-_]?identifier|social[-_]?security(?:[-_]?(?:number|no))?$|(?:first|middle|last|full|user|holder|owner)[-_]?name|e-?mail|phone|address|^street|zip[-_]?code|postal[-_]?code/i;
// Case-sensitive, because these letters also appear in other words (businessName) and in tickers used as keys (MFA).
const ACRONYM_KEY =
  /^(?:city|cvc|cvv|dob|ein|ip|otp|pan|pin|SSN|vin|zip)$|(?:^|[-_])(?:mfa|ssn)|[a-z](?:Mfa|MFA|Ssn|SSN)/;
// A form holds each value beside its name, and a biller names its own fields: { name: 'field-1', value: '123' }.
const FORM_FIELDS_KEY = /^(?:fields|parameters)$/;
// The app's default names for a dataset hold the customer's name.
const DEFAULT_DATASET_NAME = /^.{0,64}'s (?:Finances|Household)(?: \d+)?$/;

const JWT = /\beyJ[\w-]{6,}\.[\w-]{6,}(?:\.[\w-]*)?/g;
const AUTH_HEADER_VALUE = /\b(Bearer|Basic)\s+[\w.~+/=-]{16,}/gi;
const SECRET_PARAM =
  /((?:token|secret|password|passcode|credential|signature|session_state|e-?mail|user_?name|ssn|(?<=[?&#])code)[\w-]{0,64}=)(?!\s*[&"'])[^&\s"'`]+/gi;
const EMAIL = /[\w.+-]{1,64}(?:@|%40)[\w-]+\.[\w.-]+/g;
const SSN = /\b\d{3}-\d{2}-\d{4}\b/g;

const isCredentialKey = (key: string): boolean => CREDENTIAL_KEY.test(key) || ACRONYM_KEY.test(key);

/** Masks credentials in free text, such as a console line or a URL. Can't see a value with no recognizable name. */
export const stripSecretsFromText = (text: string): string =>
  text
    .replace(JWT, MASK)
    .replace(AUTH_HEADER_VALUE, `$1 ${MASK}`)
    .replace(SECRET_PARAM, `$1${MASK}`)
    .replace(EMAIL, MASK)
    .replace(SSN, MASK);

// Empties a value instead of dropping its field, because the app reads these fields once a file is loaded. Flags stay:
// they're no secret, screens read them (is this email verified?), and dehydrated Immutable values are marked with them.
const emptyValue = (value: unknown): unknown => {
  if (typeof value === 'string') return value && !value.startsWith('#UNDEFINED_') ? MASK : value;
  if (typeof value === 'number') return 0;
  if (Array.isArray(value)) return value.filter((item) => typeof item === 'string' && item.startsWith('#IMMUTABLE_'));
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).filter(([, field]) => typeof field === 'boolean'));
  }
  return value;
};

/** A copy of a JSON-like value without credentials or contact details. `key` is the name of the field holding it. */
export function stripCredentials(value: unknown, key = ''): unknown {
  if (value === null || value === undefined || typeof value === 'boolean') return value;
  if (isCredentialKey(key)) return emptyValue(value);
  if (typeof value === 'string') {
    // A body kept as a string, such as a failed request's, holds its credentials under names.
    if (/^\s*[{[]/.test(value)) {
      try {
        return JSON.stringify(stripCredentials(JSON.parse(value)));
      } catch {
        // Not JSON after all.
      }
    }
    return DEFAULT_DATASET_NAME.test(value) ? MASK : stripSecretsFromText(value);
  }
  if (Array.isArray(value)) return value.map((item) => stripCredentials(item, key));
  if (typeof value === 'object') {
    const isFormField = FORM_FIELDS_KEY.test(key);
    // A sign-in request holds its one-time code under a name that error codes share.
    const isSignInRequest = 'grantType' in value;
    return Object.fromEntries(
      Object.entries(value).map(([name, field]) => [
        name,
        (isFormField && name === 'value') || (isSignInRequest && name === 'code')
          ? emptyValue(field)
          : stripCredentials(field, name),
      ]),
    );
  }
  return value;
}

/** Local or session storage without credentials or contact details. */
export const readStorageWithoutCredentials = (storage: Record<string, string>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(storage).map(([key, value]) => [key, stripCredentials(value, key)]));
